import type {
  OperationalCalendarBooking,
  OperationalCalendarDispatch,
  OperationalCalendarStaff,
} from "@bednbody/types";

export const CALENDAR_START_MINUTES = 14 * 60;
export const CALENDAR_END_MINUTES = 24 * 60;
export const CALENDAR_SLOT_MINUTES = 30;
export const CALENDAR_TIME_ZONE = "Asia/Manila";

const MANILA_PARTS = new Intl.DateTimeFormat("en-US", {
  timeZone: CALENDAR_TIME_ZONE,
  hourCycle: "h23",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
});

export interface CalendarSlot {
  key: string;
  label: string;
  minutes: number;
}

export interface CalendarStaffName {
  id: string;
  name: string;
  hubName: string | null;
}

export interface CalendarBlock {
  id: string;
  kind: "pickup" | "drop_off" | "session" | "return";
  startsAt: string;
  endsAt: string;
  label: string;
  status: string;
}

export interface CalendarStaffRow {
  id: string;
  role: "therapist" | "rider";
  name: string;
  hubId: string;
  hubName: string | null;
  unavailable: boolean;
  blocks: CalendarBlock[];
}

export interface CalendarProjection {
  therapists: CalendarStaffRow[];
  ridersByHub: { hubId: string; hubName: string; rows: CalendarStaffRow[] }[];
  unassignedBookings: CalendarBlock[];
}

/**
 * Only the newest published version of each hub's week. The staff view returns
 * EVERY published version (0083), so a week re-published twice drew each
 * person three times. A newer version replaces the whole week, so an entry
 * from an older one is not a second shift — it is history.
 */
export function latestPublishedEntries(
  entries: readonly OperationalCalendarStaff[],
): OperationalCalendarStaff[] {
  const newest = new Map<string, number>();
  for (const entry of entries) {
    const key = `${entry.hubId}|${entry.weekStart}`;
    newest.set(key, Math.max(newest.get(key) ?? 0, entry.versionNumber));
  }
  return entries.filter(
    (entry) => entry.versionNumber === newest.get(`${entry.hubId}|${entry.weekStart}`),
  );
}

function manilaParts(value: string | Date): Record<string, string> {
  return Object.fromEntries(
    MANILA_PARTS.formatToParts(typeof value === "string" ? new Date(value) : value).map((item) => [
      item.type,
      item.value,
    ]),
  );
}

export function localDateIso(value: Date = new Date()): string {
  const parts = manilaParts(value);
  return `${parts.year}-${parts.month}-${parts.day}`;
}

export function addCalendarDays(day: string, amount: number): string {
  const date = new Date(`${day}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + amount);
  return date.toISOString().slice(0, 10);
}

export function dayStartIso(day: string): string {
  return `${day}T00:00:00+08:00`;
}

export function calendarDateLabel(day: string): string {
  return new Intl.DateTimeFormat("en-PH", {
    timeZone: CALENDAR_TIME_ZONE,
    dateStyle: "full",
  }).format(new Date(dayStartIso(day)));
}

export function calendarDateOptionLabel(day: string): string {
  return new Intl.DateTimeFormat("en-PH", {
    timeZone: CALENDAR_TIME_ZONE,
    dateStyle: "medium",
  }).format(new Date(dayStartIso(day)));
}

export function calendarSlots(): CalendarSlot[] {
  return Array.from(
    { length: (CALENDAR_END_MINUTES - CALENDAR_START_MINUTES) / CALENDAR_SLOT_MINUTES },
    (_, index) => {
      const minutes = CALENDAR_START_MINUTES + index * CALENDAR_SLOT_MINUTES;
      const hour = Math.floor(minutes / 60);
      const minute = minutes % 60;
      return {
        key: `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`,
        label: formatClock(minutes),
        minutes,
      };
    },
  );
}

export function formatClock(minutes: number): string {
  const hour = Math.floor(minutes / 60);
  const minute = minutes % 60;
  const suffix = hour >= 12 ? "PM" : "AM";
  const displayHour = hour % 12 || 12;
  return `${displayHour}:${String(minute).padStart(2, "0")} ${suffix}`;
}

export function minutesInManilaDay(value: string): number {
  const parts = manilaParts(value);
  return Number(parts.hour) * 60 + Number(parts.minute);
}

export function blockStylePosition(
  startsAt: string,
  endsAt: string,
): {
  topPercent: number;
  heightPercent: number;
} {
  const start = Math.max(CALENDAR_START_MINUTES, minutesInManilaDay(startsAt));
  const rawEnd = minutesInManilaDay(endsAt);
  const end = Math.min(CALENDAR_END_MINUTES, rawEnd < start ? CALENDAR_END_MINUTES : rawEnd);
  const span = CALENDAR_END_MINUTES - CALENDAR_START_MINUTES;
  return {
    topPercent: ((start - CALENDAR_START_MINUTES) / span) * 100,
    heightPercent: Math.max(((end - start) / span) * 100, 2),
  };
}

export function isStaffUnavailable(
  entries: OperationalCalendarStaff[],
  staffId: string,
  day: string,
): boolean {
  const entry = entries.find((item) => item.staffId === staffId && item.scheduleDate === day);
  return !entry || entry.kind === "rest_day" || !entry.dutyStart || !entry.dutyEnd;
}

function bookingBlock(booking: OperationalCalendarBooking): CalendarBlock {
  return {
    id: `session:${booking.bookingId}`,
    kind: "session",
    startsAt: booking.startsAt,
    endsAt: booking.endsAt,
    label: `${booking.ref} · ${booking.serviceName}`,
    status: booking.status,
  };
}

function dispatchBlock(dispatch: OperationalCalendarDispatch): CalendarBlock {
  return {
    id: `dispatch:${dispatch.dispatchLegId}`,
    kind: dispatch.kind,
    startsAt: dispatch.plannedStart,
    endsAt: dispatch.plannedEnd,
    label: `${dispatch.kind === "drop_off" ? "Drop-off" : dispatch.kind[0]?.toUpperCase() + dispatch.kind.slice(1)} · ${dispatch.bookingId.slice(0, 8)}`,
    status: dispatch.legStatus,
  };
}

export function buildCalendarProjection(
  staff: OperationalCalendarStaff[],
  bookings: OperationalCalendarBooking[],
  dispatches: OperationalCalendarDispatch[],
  names: readonly CalendarStaffName[],
  day: string,
): CalendarProjection {
  const nameById = new Map(names.map((item) => [item.id, item]));
  const blocksByStaff = new Map<string, CalendarBlock[]>();
  const addBlock = (staffId: string, block: CalendarBlock) => {
    const blocks = blocksByStaff.get(staffId) ?? [];
    blocks.push(block);
    blocksByStaff.set(staffId, blocks);
  };

  const dayBookings = bookings.filter(
    (booking) => localDateIso(new Date(booking.startsAt)) === day,
  );
  for (const booking of dayBookings) {
    const block = bookingBlock(booking);
    if (booking.therapistId) addBlock(booking.therapistId, block);
  }
  for (const dispatch of dispatches) {
    if (localDateIso(new Date(dispatch.plannedStart)) === day)
      addBlock(dispatch.riderId, dispatchBlock(dispatch));
  }

  const rows = staff
    .filter(
      (entry) =>
        entry.scheduleDate === day &&
        (entry.staffRole === "therapist" || entry.staffRole === "rider"),
    )
    .map((entry) => {
      const name = nameById.get(entry.staffId);
      return {
        id: entry.staffId,
        role: entry.staffRole,
        name: name?.name ?? "Staff member",
        hubId: entry.hubId,
        hubName: name?.hubName ?? null,
        unavailable:
          entry.kind === "rest_day" || !entry.dutyStart || !entry.dutyEnd || !entry.staffActive,
        blocks: (blocksByStaff.get(entry.staffId) ?? []).sort((a, b) =>
          a.startsAt.localeCompare(b.startsAt),
        ),
      } satisfies CalendarStaffRow;
    });

  const therapists = rows.filter((row) => row.role === "therapist");
  const ridersByHub = [
    ...new Set(rows.filter((row) => row.role === "rider").map((row) => row.hubId)),
  ]
    .sort()
    .map((hubId) => {
      const hubRows = rows.filter((row) => row.role === "rider" && row.hubId === hubId);
      return {
        hubId,
        hubName: hubRows[0]?.hubName ?? "Unknown hub",
        rows: hubRows,
      };
    });

  return {
    therapists,
    ridersByHub,
    unassignedBookings: dayBookings.filter((booking) => !booking.therapistId).map(bookingBlock),
  };
}
